// HerbalSoapIngredients.js - Herbal soap themed Ingredients section component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a herbal soap themed Ingredients section with ingredient cards
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the ingredients section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The ingredients elements
 */
export const createHerbalSoapIngredientsElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const ingredientsSectionClass = 'herbal-soap-ingredients-section';
  const ingredientsContainerClass = 'herbal-soap-ingredients-container';
  const ingredientsHeaderClass = 'herbal-soap-ingredients-header';
  const ingredientsTitleClass = 'herbal-soap-ingredients-title';
  const ingredientsSubtitleClass = 'herbal-soap-ingredients-subtitle';
  const ingredientsGridClass = 'herbal-soap-ingredients-grid';
  const ingredientCardClass = 'herbal-soap-ingredient-card';
  const ingredientImageWrapperClass = 'herbal-soap-ingredient-image-wrapper';
  const ingredientImageClass = 'herbal-soap-ingredient-image';
  const ingredientContentClass = 'herbal-soap-ingredient-content';
  const ingredientNameClass = 'herbal-soap-ingredient-name';
  const ingredientBenefitsClass = 'herbal-soap-ingredient-benefits';
  const ingredientDescriptionClass = 'herbal-soap-ingredient-description';
  const ingredientBadgeClass = 'herbal-soap-ingredient-badge';

  // Desktop Styles - Herbal theme with natural green colors
  const ingredientsSectionStyles = {
    padding: '120px 20px',
    backgroundColor: '#f8fffe',
    background: `
      linear-gradient(135deg, #f8fffe 0%, #f0fdf4 50%, #ecfdf5 100%),
      radial-gradient(circle at 20% 80%, rgba(144, 238, 144, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(34, 139, 34, 0.05) 0%, transparent 50%)
    `,
    position: 'relative',
    overflow: 'hidden'
  };

  const ingredientsContainerStyles = {
    maxWidth: '1400px',
    margin: '0 auto',
    width: '100%'
  };

  const ingredientsHeaderStyles = {
    textAlign: 'center',
    marginBottom: '80px'
  };

  const ingredientsTitleStyles = {
    fontSize: '48px',
    fontWeight: '700',
    color: '#1a3d1a',
    marginBottom: '20px',
    fontFamily: '"Inter", sans-serif',
    lineHeight: '1.2'
  };

  const ingredientsSubtitleStyles = {
    fontSize: '20px',
    color: '#4a5568',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif'
  };

  const ingredientsGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '24px',
    alignItems: 'start'
  };

  const ingredientCardStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
    transition: 'all 0.3s ease',
    border: '1px solid rgba(144, 238, 144, 0.15)',
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  };

  const ingredientCardHoverStyles = {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(144, 238, 144, 0.25)',
    borderColor: 'rgba(144, 238, 144, 0.3)'
  };

  const ingredientImageWrapperStyles = {
    position: 'relative',
    width: '120px',
    height: '120px',
    flexShrink: '0',
    overflow: 'hidden'
  };

  const ingredientImageStyles = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'all 0.3s ease'
  };

  const ingredientImageHoverStyles = {
    transform: 'scale(1.05)'
  };

  const ingredientBadgeStyles = {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: '#90EE90',
    color: '#1a3d1a',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '10px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    boxShadow: '0 2px 8px rgba(144, 238, 144, 0.3)'
  };

  const ingredientContentStyles = {
    padding: '20px 24px',
    flex: '1'
  };

  const ingredientNameStyles = {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1a3d1a',
    marginBottom: '8px',
    fontFamily: '"Inter", sans-serif'
  };

  const ingredientBenefitsStyles = {
    fontSize: '12px',
    color: '#228b22',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px',
    fontFamily: '"Inter", sans-serif'
  };

  const ingredientDescriptionStyles = {
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif',
    display: '-webkit-box',
    WebkitLineClamp: '3',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  };

  // Tablet Styles
  const tabletIngredientsGridStyles = {
    ...ingredientsGridStyles,
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px'
  };

  const tabletIngredientsTitleStyles = {
    ...ingredientsTitleStyles,
    fontSize: '40px'
  };

  const tabletIngredientsSubtitleStyles = {
    ...ingredientsSubtitleStyles,
    fontSize: '18px'
  };

  const tabletIngredientImageWrapperStyles = {
    ...ingredientImageWrapperStyles,
    width: '100px',
    height: '100px'
  };

  const tabletIngredientContentStyles = {
    ...ingredientContentStyles,
    padding: '16px 20px'
  };

  // Mobile Styles
  const mobileIngredientsSectionStyles = {
    ...ingredientsSectionStyles,
    padding: '80px 20px'
  };

  const mobileIngredientsHeaderStyles = {
    ...ingredientsHeaderStyles,
    marginBottom: '60px'
  };

  const mobileIngredientsTitleStyles = {
    ...ingredientsTitleStyles,
    fontSize: '32px'
  };

  const mobileIngredientsSubtitleStyles = {
    ...ingredientsSubtitleStyles,
    fontSize: '16px'
  };

  const mobileIngredientsGridStyles = {
    ...ingredientsGridStyles,
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px'
  };

  const mobileIngredientCardStyles = {
    ...ingredientCardStyles,
    flexDirection: 'column',
    textAlign: 'center'
  };

  const mobileIngredientImageWrapperStyles = {
    ...ingredientImageWrapperStyles,
    width: '100%',
    height: '120px'
  };

  const mobileIngredientContentStyles = {
    ...ingredientContentStyles,
    padding: '16px 12px'
  };

  const mobileIngredientNameStyles = {
    ...ingredientNameStyles,
    fontSize: '16px',
    marginBottom: '6px'
  };

  const mobileIngredientBenefitsStyles = {
    ...ingredientBenefitsStyles,
    fontSize: '10px',
    marginBottom: '8px'
  };

  const mobileIngredientDescriptionStyles = {
    ...ingredientDescriptionStyles,
    fontSize: '12px',
    WebkitLineClamp: '2'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(ingredientsSectionClass, ingredientsSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(ingredientsContainerClass, ingredientsContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(ingredientsHeaderClass, ingredientsHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(ingredientsTitleClass, ingredientsTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(ingredientsSubtitleClass, ingredientsSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(ingredientsGridClass, ingredientsGridStyles);
    newRegistry[DEVICES.DESKTOP].set(ingredientCardClass, ingredientCardStyles);
    newRegistry[DEVICES.DESKTOP].set(ingredientImageWrapperClass, ingredientImageWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(ingredientImageClass, ingredientImageStyles);
    newRegistry[DEVICES.DESKTOP].set(ingredientBadgeClass, ingredientBadgeStyles);
    newRegistry[DEVICES.DESKTOP].set(ingredientContentClass, ingredientContentStyles);
    newRegistry[DEVICES.DESKTOP].set(ingredientNameClass, ingredientNameStyles);
    newRegistry[DEVICES.DESKTOP].set(ingredientBenefitsClass, ingredientBenefitsStyles);
    newRegistry[DEVICES.DESKTOP].set(ingredientDescriptionClass, ingredientDescriptionStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${ingredientCardClass}--hover`, ingredientCardHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${ingredientImageClass}--hover`, ingredientImageHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(ingredientsGridClass, tabletIngredientsGridStyles);
    newRegistry[DEVICES.TABLET].set(ingredientsTitleClass, tabletIngredientsTitleStyles);
    newRegistry[DEVICES.TABLET].set(ingredientsSubtitleClass, tabletIngredientsSubtitleStyles);
    newRegistry[DEVICES.TABLET].set(ingredientImageWrapperClass, tabletIngredientImageWrapperStyles);
    newRegistry[DEVICES.TABLET].set(ingredientContentClass, tabletIngredientContentStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(ingredientsSectionClass, mobileIngredientsSectionStyles);
    newRegistry[DEVICES.MOBILE].set(ingredientsHeaderClass, mobileIngredientsHeaderStyles);
    newRegistry[DEVICES.MOBILE].set(ingredientsTitleClass, mobileIngredientsTitleStyles);
    newRegistry[DEVICES.MOBILE].set(ingredientsSubtitleClass, mobileIngredientsSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(ingredientsGridClass, mobileIngredientsGridStyles);
    newRegistry[DEVICES.MOBILE].set(ingredientCardClass, mobileIngredientCardStyles);
    newRegistry[DEVICES.MOBILE].set(ingredientImageWrapperClass, mobileIngredientImageWrapperStyles);
    newRegistry[DEVICES.MOBILE].set(ingredientContentClass, mobileIngredientContentStyles);
    newRegistry[DEVICES.MOBILE].set(ingredientNameClass, mobileIngredientNameStyles);
    newRegistry[DEVICES.MOBILE].set(ingredientBenefitsClass, mobileIngredientBenefitsStyles);
    newRegistry[DEVICES.MOBILE].set(ingredientDescriptionClass, mobileIngredientDescriptionStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    ingredientsSectionClass,
    ingredientsContainerClass,
    ingredientsHeaderClass,
    ingredientsTitleClass,
    ingredientsSubtitleClass,
    ingredientsGridClass,
    ingredientCardClass,
    ingredientImageWrapperClass,
    ingredientImageClass,
    ingredientBadgeClass,
    ingredientContentClass,
    ingredientNameClass,
    ingredientBenefitsClass,
    ingredientDescriptionClass
  ]));

  // Generate IDs for all elements
  const ingredientsSectionId = Date.now();
  const ingredientsContainerId = ingredientsSectionId + 1;
  const ingredientsHeaderId = ingredientsSectionId + 2;
  const ingredientsTitleId = ingredientsSectionId + 3;
  const ingredientsSubtitleId = ingredientsSectionId + 4;
  const ingredientsGridId = ingredientsSectionId + 5;

  // Ingredients data
  const ingredients = [
    {
      name: 'Turmeric',
      benefits: 'Anti-inflammatory & Brightening',
      description: 'Known for its powerful anti-inflammatory and antioxidant properties, turmeric helps brighten skin tone and reduce blemishes naturally.',
      image: 'https://images.unsplash.com/photo-1615485500704-8e990f9900f7?q=80&w=2187&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      badge: 'Organic'
    },
    {
      name: 'Neem',
      benefits: 'Antibacterial & Purifying',
      description: 'A natural antibacterial agent that purifies the skin, fights acne, and helps maintain healthy, clear complexion.',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=2187&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      badge: 'Pure'
    },
    {
      name: 'Aloe Vera',
      benefits: 'Moisturizing & Healing',
      description: 'Provides deep hydration and promotes skin healing with its soothing gel that calms irritation and restores moisture.',
      image: 'https://images.unsplash.com/photo-1596755389378-c31d21fd1273?q=80&w=2187&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      badge: 'Natural'
    },
    {
      name: 'Coconut Oil',
      benefits: 'Nourishing & Protective',
      description: 'Rich in vitamins and fatty acids, coconut oil deeply nourishes skin while creating a protective barrier against environmental damage.',
      image: 'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?q=80&w=2187&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      badge: 'Virgin'
    },
    {
      name: 'Lavender',
      benefits: 'Calming & Aromatherapy',
      description: 'Offers natural aromatherapy benefits while soothing sensitive skin and promoting relaxation during your skincare routine.',
      image: 'https://images.unsplash.com/photo-1611909023032-2d6b3134ecba?q=80&w=2187&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      badge: 'Essential'
    },
    {
      name: 'Honey',
      benefits: 'Antimicrobial & Hydrating',
      description: 'Natural humectant that draws moisture to the skin while providing antimicrobial properties for healthy, glowing complexion.',
      image: 'https://images.unsplash.com/photo-1587049352846-4a222e784d38?q=80&w=2187&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      badge: 'Raw'
    }
  ];

  // Create ingredient card elements
  let currentId = ingredientsGridId + 1;
  const ingredientElements = [];

  ingredients.forEach((ingredient, index) => {
    const cardId = currentId++;
    const imageWrapperId = currentId++;
    const imageId = currentId++;
    const badgeId = currentId++;
    const contentId = currentId++;
    const nameId = currentId++;
    const benefitsId = currentId++;
    const descriptionId = currentId++;

    // Ingredient Card
    ingredientElements.push({
      id: cardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `${ingredient.name} Card`,
      parentId: ingredientsGridId,
      hierarchyName: `${ingredient.name} Card`,
      className: ingredientCardClass,
      styles: {},
      children: [imageWrapperId, contentId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-ingredient-card'
      }
    });

    // Image Wrapper
    ingredientElements.push({
      id: imageWrapperId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `${ingredient.name} Image Wrapper`,
      parentId: cardId,
      hierarchyName: `${ingredient.name} Image Wrapper`,
      className: ingredientImageWrapperClass,
      styles: {},
      children: [imageId, badgeId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-ingredient-image-wrapper'
      }
    });

    // Ingredient Image
    ingredientElements.push({
      id: imageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: `${ingredient.name} Image`,
      parentId: imageWrapperId,
      hierarchyName: `${ingredient.name} Image`,
      className: ingredientImageClass,
      styles: {},
      children: [],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      src: ingredient.image,
      attributes: {
        'data-element': 'herbal-soap-ingredient-image',
        'alt': ingredient.name
      }
    });

    // Ingredient Badge
    ingredientElements.push({
      id: badgeId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: `${ingredient.name} Badge`,
      parentId: imageWrapperId,
      hierarchyName: `${ingredient.name} Badge`,
      className: ingredientBadgeClass,
      styles: {},
      children: [],
      text: ingredient.badge,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-ingredient-badge'
      }
    });

    // Ingredient Content
    ingredientElements.push({
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `${ingredient.name} Content`,
      parentId: cardId,
      hierarchyName: `${ingredient.name} Content`,
      className: ingredientContentClass,
      styles: {},
      children: [nameId, benefitsId, descriptionId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-ingredient-content'
      }
    });

    // Ingredient Name
    ingredientElements.push({
      id: nameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: `${ingredient.name} Name`,
      parentId: contentId,
      hierarchyName: `${ingredient.name} Name`,
      className: ingredientNameClass,
      styles: {},
      children: [],
      text: ingredient.name,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-ingredient-name'
      }
    });

    // Ingredient Benefits
    ingredientElements.push({
      id: benefitsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `${ingredient.name} Benefits`,
      parentId: contentId,
      hierarchyName: `${ingredient.name} Benefits`,
      className: ingredientBenefitsClass,
      styles: {},
      children: [],
      text: ingredient.benefits,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-ingredient-benefits'
      }
    });

    // Ingredient Description
    ingredientElements.push({
      id: descriptionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: `${ingredient.name} Description`,
      parentId: contentId,
      hierarchyName: `${ingredient.name} Description`,
      className: ingredientDescriptionClass,
      styles: {},
      children: [],
      text: ingredient.description,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-ingredient-description'
      }
    });
  });

  // Get ingredient card IDs for the grid
  const ingredientCardIds = ingredientElements
    .filter(element => element.parentId === ingredientsGridId)
    .map(element => element.id);

  // Create base elements
  const baseElements = [
    // Main Ingredients Section
    {
      id: ingredientsSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Herbal Soap Ingredients Section',
      parentId: null,
      hierarchyName: 'Herbal Soap Ingredients Section',
      className: ingredientsSectionClass,
      styles: {},
      children: [ingredientsContainerId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-ingredients-section',
        'data-component': 'section',
        'data-section-type': 'ingredients',
        'data-page-id': currentPageId
      }
    },

    // Container
    {
      id: ingredientsContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Ingredients Container',
      parentId: ingredientsSectionId,
      hierarchyName: 'Ingredients Container',
      className: ingredientsContainerClass,
      styles: {},
      children: [ingredientsHeaderId, ingredientsGridId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-ingredients-container'
      }
    },

    // Header
    {
      id: ingredientsHeaderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Ingredients Header',
      parentId: ingredientsContainerId,
      hierarchyName: 'Ingredients Header',
      className: ingredientsHeaderClass,
      styles: {},
      children: [ingredientsTitleId, ingredientsSubtitleId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-ingredients-header'
      }
    },

    // Title
    {
      id: ingredientsTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Ingredients Title',
      parentId: ingredientsHeaderId,
      hierarchyName: 'Ingredients Title',
      className: ingredientsTitleClass,
      styles: {},
      children: [],
      text: 'Pure Natural Ingredients',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-ingredients-title'
      }
    },

    // Subtitle
    {
      id: ingredientsSubtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Ingredients Subtitle',
      parentId: ingredientsHeaderId,
      hierarchyName: 'Ingredients Subtitle',
      className: ingredientsSubtitleClass,
      styles: {},
      children: [],
      text: 'Each ingredient is carefully selected for its unique healing properties and skin benefits. We source only the finest organic herbs and natural extracts to create our premium soap formulations.',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-ingredients-subtitle'
      }
    },

    // Ingredients Grid
    {
      id: ingredientsGridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Ingredients Grid',
      parentId: ingredientsContainerId,
      hierarchyName: 'Ingredients Grid',
      className: ingredientsGridClass,
      styles: {},
      children: ingredientCardIds,
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-ingredients-grid'
      }
    }
  ];

  // Combine all elements
  const allElements = [...baseElements, ...ingredientElements];
  
  // Return the created elements
  return {
    elements: allElements
  };
}; 